// capacity.js — the derivations behind the Engine screen (K7) and the Tuning
// screen (K8), as plain functions.
//
// WHY THE LOGIC IS NOT IN THE .jsx. This repo has no component test runner, and
// adding one is a dependency decision nobody has made. But "no runner" must not
// mean "no tests" — every defect this integration has produced came from an
// inference nobody could execute. So the SHAPING lives here, in plain ESM that
// `node src/capacity.test.js` can import directly, and the components stay thin
// enough to be read.
//
// WHAT THIS FILE REFUSES TO DO
//
//   It does not invent a fleet. K1–K3 (the registry, the provisioner, the
//   reconciler) are Phase 4; today there is ONE endpoint behind a static
//   resolver. A screen that rendered a "fleet" of one would be a picture of a
//   system that does not exist, and the first person to read it would size
//   capacity against it.
//
//   It does not hardcode the precedence chain. The backend publishes
//   `precedence_high_to_low`, and an order that exists in two places drifts —
//   with the copy in the UI being the one nobody tests.
//
//   It does not present a single replica's queue as the fleet's. `/admin/queue`
//   answers from the process that served the request. That is exact today
//   (one backend process) and becomes a fraction the moment a second replica
//   exists, so the limitation is rendered, not assumed away. See §AF: when D6
//   moves job state to the shared store, the `now` half moves with it.

/** Tier order used only when the backend does not publish one — which should
 *  never happen, and is visible as `assumed` rather than silently correct. */
export const FALLBACK_PRECEDENCE = ['request', 'org', 'shop', 'deployment'];

const MODE_COPY = {
  remote: {
    label: 'Engine service',
    tone: 'info',
    detail: 'Nests run in a separate engine container over HTTP. This process '
          + 'holds no engine lock.',
  },
  in_process: {
    label: 'In-process',
    tone: '',
    detail: 'Nests run inside the backend, serialised by the process-wide '
          + 'engine lock. This is also the rollback state.',
  },
  misconfigured: {
    label: 'Misconfigured',
    tone: 'danger',
    detail: 'MNE_ENGINE_ENDPOINT is set but unusable, so every plan is being '
          + 'REFUSED. It is not falling back to in-process — falling back '
          + 'would look exactly like the engine service working.',
  },
  unknown: {
    label: 'Unknown',
    tone: 'warn',
    detail: 'The backend could not resolve which engine path is live. Treat '
          + 'any plan failure as unexplained until this reads something else.',
  },
};

/**
 * The live engine path, from GET /health.
 *
 * Tolerant of a backend that predates `engine_path` (it reports `unknown`
 * rather than guessing `in_process`): assuming the old default would be a
 * confident answer about a deployment we cannot see, which is the exact defect
 * the backend's own engine_path work existed to fix.
 */
export function enginePathSummary(health) {
  const path = health?.engine_path || {};
  const mode = MODE_COPY[path.mode] ? path.mode : 'unknown';
  const conc = health?.concurrency || {};
  return {
    mode,
    ...MODE_COPY[mode],
    resolver: path.resolver || null,
    // Present only on the authenticated route; the open /health withholds it
    // on purpose (states, not addresses).
    endpoint: path.endpoint || null,
    error: path.error || null,
    engineVersion: health?.engine_version || null,
    harnessVersion: health?.harness_version || null,
    engineAccess: conc.engine_access || null,
    // `true_parallelism` is deliberately tri-state on the backend: false
    // in-process, and NULL on the remote path because this process cannot see
    // the replica count. Rendering null as "no" would restate the in-process
    // answer on a path where it is not known.
    trueParallelism: conc.true_parallelism === undefined
      ? null : conc.true_parallelism,
    parallelismLimitedBy: conc.parallelism_limited_by || null,
    scaleOut: conc.scale_out || null,
  };
}

/** `0.4231` → `'42.3%'`; null/undefined → `'—'`.
 *
 *  NULL IS NOT ZERO. The backend sends null when the denominator is zero —
 *  "nothing ran, ask again later" — and rendering that as `0.0%` would read as
 *  an org that consumes nothing. That is a capacity decision made on a
 *  division by zero. */
export function sharePct(share) {
  if (share === null || share === undefined || Number.isNaN(share)) return '—';
  return `${(share * 100).toFixed(1)}%`;
}

/**
 * One row per org, joining the windowed `load` with the live `now` half.
 *
 * Sorted by worker-second share descending, because the question the screen
 * exists to answer — which tenant is consuming the shared pool — is answered
 * by reading the top of the list. Orgs that appear only in the live half (they
 * have work in flight but nothing finished in the window) sort last rather
 * than being dropped: a queue nobody can see is worse than an unranked row.
 */
export function capacityRows(queue) {
  const load = queue?.load || {};
  const live = queue?.now?.by_org || {};
  const ids = new Set([...Object.keys(load), ...Object.keys(live)]);
  const rows = [];
  ids.forEach((orgId) => {
    const l = load[orgId] || {};
    const n = live[orgId] || {};
    rows.push({
      orgId,
      // '-' is the backend's marker for work with no tenant attribution. It is
      // real work occupying a real worker, so it is shown rather than dropped —
      // dropping it would make the shares add up to less than the pool.
      orgName: l.org_name || n.org_name || (orgId === '-' ? 'Unattributed' : orgId),
      jobs: l.jobs || 0,
      failed: l.failed || 0,
      workerSeconds: l.worker_seconds ?? 0,
      mneCalls: l.mne_calls || 0,
      shareOfWorkerSeconds: l.share_of_worker_seconds ?? null,
      shareOfMneCalls: l.share_of_mne_calls ?? null,
      queued: n.queued || 0,
      running: n.running || 0,
      queuedOldestWaitS: n.queued_oldest_wait_s ?? null,
      runningOldestAgeS: n.running_oldest_age_s ?? null,
    });
  });
  rows.sort((a, b) => (b.shareOfWorkerSeconds ?? -1) - (a.shareOfWorkerSeconds ?? -1)
                   || b.workerSeconds - a.workerSeconds
                   || String(a.orgName).localeCompare(String(b.orgName)));
  return rows;
}

/**
 * The per-key tier table for K8: what the org supplies, what it may supply,
 * and what it may never supply.
 *
 * `settable_keys` and `deployment_only_keys` come from the backend. The screen
 * does not keep its own list — the last time this project kept a second copy of
 * a backend rule, the copy was the one that was wrong for a year.
 */
export function tuningRows(payload) {
  const settable = payload?.settable_keys || [];
  const deploymentOnly = payload?.deployment_only_keys || [];
  const experiments = payload?.experiment_keys || [];
  const current = payload?.engine_config || {};
  // The backend's own reason for each "no" (the sentence its PATCH answers
  // with). An operator told "no" without a reason asks again by editing the
  // database. The copy below is only for a backend too old to publish one.
  const reasons = payload?.refusal_reasons || {};
  // A key the org MAY set that the engine path does not obey — use_gls on the
  // construction-only fast layout. Offering it silently would be a switch that
  // does nothing, presented as one that works.
  const notHonoured = payload?.not_honoured || {};
  const rows = settable.map((key) => ({
    key,
    value: key in current ? current[key] : null,
    set: key in current,
    settable: true,
    notHonoured: notHonoured[key] || null,
  }));
  deploymentOnly.forEach((key) => {
    rows.push({
      key,
      value: null,
      set: false,
      settable: false,
      reason: reasons[key]
        || 'Set by the deployment only. It is a correctness switch — it '
         + 'reopens the engine\'s parallel-context window — so exactly one '
         + 'place decides it.',
    });
  });
  // §2g EXPERIMENT keys change the LAYOUT, so no organisation sets them. A row
  // that is still stored from before the ruling shows as set, so it can be
  // cleared rather than sit there unreachable.
  experiments.forEach((key) => {
    rows.push({
      key,
      value: key in current ? current[key] : null,
      set: key in current,
      settable: false,
      experiment: true,
      reason: reasons[key]
        || 'An experiment: it changes the layout, so it is set per '
         + 'organisation nowhere. Compare it with the engine comparison run.',
    });
  });
  return rows;
}

/** The chain, from the backend when it publishes one. `assumed` is true when
 *  we fell back, so a screen can say "assumed" instead of stating it. */
export function precedenceOf(payload) {
  const published = payload?.precedence_high_to_low;
  if (Array.isArray(published) && published.length) {
    return { chain: published, assumed: false };
  }
  return { chain: FALLBACK_PRECEDENCE, assumed: true };
}

/**
 * Turn a plan's `engine_config_resolution` into per-key rows.
 *
 * This is the answer to "why didn't my setting apply?", which is the question
 * a precedence chain actually gets asked. `not_applied` is carried through
 * unchanged: a key that was supplied and then dropped is the single most
 * confusing state, and it is the one the trace exists for.
 */
export function resolutionRows(resolution) {
  const effective = resolution?.effective || {};
  const suppliedBy = resolution?.supplied_by || {};
  const notApplied = resolution?.not_applied || {};
  const keys = new Set([...Object.keys(effective), ...Object.keys(notApplied)]);
  return [...keys].sort().map((key) => ({
    key,
    value: key in effective ? effective[key] : null,
    tier: suppliedBy[key] || null,
    notAppliedReason: notApplied[key] || null,
  }));
}
